/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Invoice Settings Hooks - React Query for Invoice Settings
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceSettingsService } from '@/services/superadmin/invoice-settings.service';
import { InvoiceSettings, UpdateInvoiceSettingsRequest } from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';

// Query keys
export const invoiceSettingsKeys = {
  all: ['invoice-settings'] as const,
  settings: () => [...invoiceSettingsKeys.all, 'current'] as const,
};

/**
 * Hook to fetch invoice settings
 */
export function useInvoiceSettings() {
  return useQuery<InvoiceSettings, ApiError>({
    queryKey: invoiceSettingsKeys.settings(),
    queryFn: () => invoiceSettingsService.getSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update invoice settings
 */
export function useUpdateInvoiceSettings() {
  const queryClient = useQueryClient();

  return useMutation<InvoiceSettings, ApiError, UpdateInvoiceSettingsRequest>({
    mutationFn: (request) => invoiceSettingsService.updateSettings(request),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(invoiceSettingsKeys.settings(), updatedSettings);
    },
  });
}

/**
 * Hook to upload signature image
 */
export function useUploadSignatureImage() {
  const queryClient = useQueryClient();

  return useMutation<InvoiceSettings, ApiError, File>({
    mutationFn: (file) => invoiceSettingsService.uploadSignatureImage(file),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(invoiceSettingsKeys.settings(), updatedSettings);
    },
  });
}

/**
 * Hook to delete signature image
 */
export function useDeleteSignatureImage() {
  const queryClient = useQueryClient();

  return useMutation<InvoiceSettings, ApiError, void>({
    mutationFn: () => invoiceSettingsService.deleteSignatureImage(),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(invoiceSettingsKeys.settings(), updatedSettings);
    },
  });
}
